'use client'
import { createContext, useEffect, useMemo, useState } from 'react'
import type { FC, JSX } from 'react'
import { LANG } from '@/utils'
import { useParams } from 'next/navigation'
import { getDictionary } from '@/app/dictionaries'
import type { JsonPossibleValues } from '@/schema'
import type { TypeLang } from '@/utils'

type Dictionary = JsonPossibleValues
const DEFAULT_LANG = (LANG || 'es') as TypeLang

// Import default language dictionaries statically


/**
 * Load initial dictionary synchronously with dynamic path
 * - This is required to avoid flickering on first load
 * */
const loadInitialDictionary = () => {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const project = require(`@/app/dictionaries/${DEFAULT_LANG}.json`)
  return { ...project }
}

const DEFAULT_DICTIONARY = loadInitialDictionary()

export const TranslationsContext = createContext<{ dictionary: Dictionary }>({ dictionary: DEFAULT_DICTIONARY })

type Props = {
  children: JSX.Element
}

let prevLang = DEFAULT_LANG
export const TranslationsProvider: FC<Props> = ({ children }) => {
  const { lang } = useParams() as { lang: TypeLang }
  const [dictionary, setDictionary] = useState<Dictionary>(DEFAULT_DICTIONARY)
  const currentLang = useMemo(() => lang ?? DEFAULT_LANG, [lang])

  useEffect(() => {
    async function updateDictionary(lang: TypeLang) {
      const incomingDictionary = await getDictionary(lang)
      setDictionary(incomingDictionary)
    }

    if (lang && lang !== prevLang && lang !== DEFAULT_LANG) {
      updateDictionary(lang)
      prevLang = lang
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLang])

  return (
    <TranslationsContext.Provider value={{ dictionary }}>
      {children}
    </TranslationsContext.Provider>
  )
}
